import { api } from './apiClient';

/**
 * CRM service client.
 *
 * IMPORTANT — response shapes are NOT uniform:
 *  - Leads / activities / pipeline-config rows come straight off `SELECT *`
 *    (or an explicit column list) in src/models/Crm.js, so they are
 *    snake_case on the wire (company_name, occurred_at, ...).
 *  - Pipeline/summary aggregates are hand-built objects in the model, so
 *    those ARE camelCase (totalValue, weightedForecast, ...).
 *  - Every input body (create/update payloads) is camelCase — that's what
 *    src/validators/crm.js checks for.
 * This file mirrors that split exactly rather than normalising it, so a
 * console.log of a real response matches the types below field-for-field.
 *
 * All five apiClient verbs (get/post/put/patch/delete) auto-unwrap the
 * `{status, data}` envelope down to `data` — see apiClient.ts. Most endpoints
 * still nest the payload one level deeper under a named key (e.g. `{lead}`),
 * so callers below unwrap that named key by hand.
 */

interface MessageOnly {
  status: string;
  message: string;
}

// =============================================
// LEADS
// =============================================

export const LEAD_STATUSES = ['NEW', 'CONTACTED', 'QUALIFIED', 'CONVERTED', 'LOST'] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];

/** A lead row as the API actually returns it — snake_case, plus the joined assignee name. */
export interface Lead {
  id: number;
  lead_source: string | null;
  company_name: string;
  contact_person: string | null;
  email: string | null;
  phone: string | null;
  status: LeadStatus;
  assigned_to: number | null;
  assigned_to_name?: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface LeadsPagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface LeadsListResult {
  leads: Lead[];
  pagination: LeadsPagination;
}

export interface LeadFilters {
  status?: LeadStatus;
  source?: string;
  assignedTo?: number;
  search?: string;
  page?: number;
  limit?: number;
}

export interface CreateLeadInput {
  companyName: string;
  leadSource?: string | null;
  contactPerson?: string | null;
  email?: string | null;
  phone?: string | null;
  status?: Exclude<LeadStatus, 'CONVERTED'>;
  assignedTo?: number | null;
  notes?: string | null;
}

export type UpdateLeadInput = Omit<CreateLeadInput, 'status'>;

export interface ConvertLeadInput {
  companyName?: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  gstin?: string;
  creditLimit?: number;
  creditDays?: number;
  createOpportunity?: boolean;
  opportunityName?: string;
  estimatedValue?: number;
  probability?: number;
  expectedClosureDate?: string;
  opportunityNotes?: string;
  assignedTo?: number;
}

/** Raw `customers` row (INSERT column list in Crm.convertLead + id/timestamps). */
export interface ConvertedCustomer {
  id: number;
  customer_code: string;
  company_name: string;
  contact_person: string | null;
  email: string | null;
  phone: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  country?: string | null;
  gstin?: string | null;
  credit_limit?: number | string;
  credit_days?: number | string;
  created_at?: string;
}

/** Raw `opportunities` row (`SELECT *`), as returned by convert + stage-move. */
export interface CrmOpportunityRow {
  id: number;
  customer_id: number | null;
  lead_id: number | null;
  opportunity_name: string;
  estimated_value: number | string;
  probability: number | string;
  expected_closure_date: string | null;
  stage: FunnelStage;
  assigned_to: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ConvertLeadResult {
  lead: Lead;
  customer: ConvertedCustomer;
  opportunity: CrmOpportunityRow | null;
}

// =============================================
// PIPELINE (opportunities.stage funnel)
// =============================================

export const FUNNEL_ORDER = [
  'PROSPECTING',
  'QUALIFICATION',
  'PROPOSAL',
  'NEGOTIATION',
  'WON',
  'LOST',
] as const;
export type FunnelStage = (typeof FUNNEL_ORDER)[number];

/** Backend-computed per-stage aggregate. camelCase — built by hand in Crm.getPipeline(). */
export interface PipelineStageAgg {
  stage: FunnelStage;
  count: number;
  totalValue: number;
  weightedValue: number;
}

export interface PipelineSummary {
  totalOpen: number;
  weightedForecast: number;
  /** null when no deal has ever closed — never render this as "0%". */
  winRatePct: number | null;
  avgDealSize: number | null;
  avgSalesCycleDays: number | null;
}

export interface UpdateOpportunityStageInput {
  stage: FunnelStage;
  probability?: number;
}

// =============================================
// ACTIVITIES
// =============================================

export const ACTIVITY_ENTITY_TYPES = ['lead', 'opportunity', 'customer'] as const;
export type ActivityEntityType = (typeof ACTIVITY_ENTITY_TYPES)[number];

export const ACTIVITY_TYPES = ['CALL', 'EMAIL', 'MEETING', 'NOTE', 'TASK', 'DEMO'] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];

export interface CrmActivity {
  id: number;
  entity_type: ActivityEntityType;
  entity_id: number;
  activity_type: ActivityType;
  subject: string;
  notes: string | null;
  outcome: string | null;
  occurred_at: string;
  duration_mins: number | null;
  follow_up_at: string | null;
  owner_id: number | null;
  owner_name?: string | null;
  created_at: string;
}

export interface ActivityFilters {
  entityType?: ActivityEntityType;
  entityId?: number;
  activityType?: ActivityType;
  from?: string;
  to?: string;
}

export interface CreateActivityInput {
  entityType: ActivityEntityType;
  entityId: number;
  activityType: ActivityType;
  subject: string;
  notes?: string;
  outcome?: string;
  occurredAt?: string;
  durationMins?: number;
  followUpAt?: string;
  ownerId?: number;
}

// =============================================
// DASHBOARD SUMMARY
// =============================================

export interface CrmSummary {
  newLeadsThisWeek: number;
  qualifiedLeads: number;
  /** null when there are no leads at all yet. */
  conversionRatePct: number | null;
  openOpportunities: number;
  weightedForecast: number;
  wonThisMonth: number;
  lostThisMonth: number;
  activitiesThisWeek: number;
}

// =============================================
// CONFIG — pipelines / stages
// =============================================

export interface CrmStage {
  id: number;
  pipeline_id: number;
  name: string;
  sort_order: number;
  probability_default: number | string;
  is_won: number | boolean;
  is_lost: number | boolean;
  created_at?: string;
  updated_at?: string;
}

export interface CrmPipelineConfig {
  id: number;
  name: string;
  is_default: number | boolean;
  created_at: string;
  updated_at: string;
  stages: CrmStage[];
}

export interface CreatePipelineStageInput {
  name: string;
  sortOrder?: number;
  probabilityDefault?: number;
  isWon?: boolean;
  isLost?: boolean;
}

export interface CreatePipelineInput {
  name: string;
  isDefault?: boolean;
  stages?: CreatePipelineStageInput[];
}

export interface UpdateStageInput {
  name?: string;
  sortOrder?: number;
  probabilityDefault?: number;
  isWon?: boolean;
  isLost?: boolean;
}

// =============================================
// REVENUE COMMAND CENTER / 360 / WORKSPACE / MY-DAY / CONTACTS
// (Transformation endpoints — migration 059. Backend shapes documented in
// controllers/crm.js; most nested rows are snake_case off SELECT *.)
// =============================================

export type PrioritySeverity = 'high' | 'medium' | 'low';

export interface CrmPriority {
  severity: PrioritySeverity;
  type: string;
  label: string;
  entityType?: string | null;
  entityId?: number | null;
  link?: string | null;
}

export interface CrmFeedItem {
  ts: string;
  kind: string;
  label: string;
  link?: string | null;
}

export interface CrmForecast {
  commit: number;
  bestCase: number;
  pipeline: number;
  weightedPipeline: number;
  won: number;
  lost: number;
  target: number | null;
  coveragePct: number | null;
  gap: number | null;
}

export interface CrmCommandCenter {
  period: { fy: string; from: string; to: string };
  /** Pass-through of SalesAnalytics.getOverview — keys are camelCase KPIs. */
  kpis: Record<string, any>;
  forecast: CrmForecast;
  myPriorities: CrmPriority[];
  activityFeed: CrmFeedItem[];
  hotLeads: Array<Record<string, any>>;
}

export interface HealthSignal {
  label: string;
  impact: 'positive' | 'negative' | 'neutral';
  detail?: string;
}
export interface CustomerHealth {
  score: number;
  band: string;
  signals: HealthSignal[];
}

export interface TimelineItem {
  ts: string;
  kind: string;
  label: string;
  amount?: number | null;
  link?: string | null;
}

export interface CrmCustomer360 {
  profile: Record<string, any>;
  summary: Record<string, any>;
  health: CustomerHealth;
  opportunities: { open: Array<Record<string, any>>; weightedOpenTotal: number };
  contacts: Contact[];
  leadOrigin: Record<string, any> | null;
  productsPurchased: Array<{
    productId: number;
    productName: string;
    productCode: string | null;
    qty: number;
    revenue: number;
    invoiceCount: number;
  }>;
  orders: any;
  invoices: any;
  gst: any;
  timeline: TimelineItem[];
}

export interface DealHealth {
  score: number;
  band: 'On Track' | 'At Risk' | 'Critical' | string;
  reasons: string[];
}

export interface NextBestAction {
  action: string;
  reason: string;
}

export interface RelatedRecord {
  stage: string;
  present: boolean;
  docNumber: string | null;
  amount: number | null;
  status: string | null;
  link: string | null;
}

export interface OpportunityWorkspace {
  header: Record<string, any>;
  health: DealHealth;
  nextBestAction: NextBestAction | null;
  timeline: CrmActivity[];
  contacts: Contact[];
  relatedRecords: RelatedRecord[];
}

export interface MyDayItem {
  id: number;
  entityType: ActivityEntityType;
  entityId: number;
  entityLabel: string | null;
  activityType: ActivityType;
  subject: string;
  notes: string | null;
  followUpAt: string | null;
  bucket: 'overdue' | 'dueToday' | string;
  link?: string | null;
}
export interface MyDay {
  items: MyDayItem[];
  counts: { overdue: number; dueToday: number; upcoming: number };
}

// ---- Contacts (buying committee) ----
export interface Contact {
  id: number;
  customer_id: number;
  name: string;
  designation: string | null;
  department: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  is_primary: number | boolean;
  is_decision_maker: number | boolean;
  is_influencer: number | boolean;
  is_technical: number | boolean;
  is_finance: number | boolean;
  is_procurement: number | boolean;
  relationship_strength: string | null;
  preferred_channel: string | null;
  last_contact_at: string | null;
  next_contact_at: string | null;
  notes: string | null;
  is_active: number | boolean;
  created_at?: string;
  updated_at?: string;
}

export interface CreateContactInput {
  name: string;
  designation?: string | null;
  department?: string | null;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  isPrimary?: boolean;
  isDecisionMaker?: boolean;
  isInfluencer?: boolean;
  isTechnical?: boolean;
  isFinance?: boolean;
  isProcurement?: boolean;
  relationshipStrength?: string;
  preferredChannel?: string;
  notes?: string;
}
export type UpdateContactInput = Partial<CreateContactInput> & { isActive?: boolean };

// =============================================
// SERVICE
// =============================================

export const crmService = {
  // ---- Leads ----
  getLeads: (filters: LeadFilters = {}): Promise<LeadsListResult> =>
    api.get<LeadsListResult>('/crm/leads', { params: filters }),

  getLead: (id: number): Promise<Lead> =>
    api.get<{ lead: Lead }>(`/crm/leads/${id}`).then((r) => r.lead),

  createLead: (data: CreateLeadInput): Promise<Lead> =>
    api.post<{ lead: Lead }>('/crm/leads', data).then((r) => r.lead),

  updateLead: (id: number, data: UpdateLeadInput): Promise<Lead> =>
    api.put<{ lead: Lead }>(`/crm/leads/${id}`, data).then((r) => r.lead),

  updateLeadStatus: (id: number, status: Exclude<LeadStatus, 'CONVERTED'>): Promise<Lead> =>
    api
      .patch<{ lead: Lead }>(`/crm/leads/${id}/status`, { status })
      .then((r) => r.lead),

  deleteLead: (id: number): Promise<void> =>
    api.delete<MessageOnly>(`/crm/leads/${id}`).then(() => undefined),

  convertLead: (id: number, data: ConvertLeadInput = {}): Promise<ConvertLeadResult> =>
    api.post<ConvertLeadResult>(`/crm/leads/${id}/convert`, data),

  // ---- Pipeline ----
  getPipeline: (): Promise<PipelineStageAgg[]> =>
    api.get<{ pipeline: PipelineStageAgg[] }>('/crm/pipeline').then((r) => r.pipeline),

  getPipelineSummary: (): Promise<PipelineSummary> =>
    api.get<PipelineSummary>('/crm/pipeline/summary'),

  updateOpportunityStage: (
    id: number,
    data: UpdateOpportunityStageInput
  ): Promise<CrmOpportunityRow> =>
    api
      .patch<{ opportunity: CrmOpportunityRow }>(`/crm/opportunities/${id}/stage`, data)
      .then((r) => r.opportunity),

  // ---- Activities ----
  getActivities: (filters: ActivityFilters = {}): Promise<{ activities: CrmActivity[]; count: number }> =>
    api.get<{ activities: CrmActivity[]; count: number }>('/crm/activities', { params: filters }),

  getUpcomingActivities: (): Promise<{ activities: CrmActivity[]; count: number }> =>
    api.get<{ activities: CrmActivity[]; count: number }>('/crm/activities/upcoming'),

  createActivity: (data: CreateActivityInput): Promise<CrmActivity> =>
    api.post<{ activity: CrmActivity }>('/crm/activities', data).then((r) => r.activity),

  // ---- Dashboard ----
  getSummary: (): Promise<CrmSummary> => api.get<CrmSummary>('/crm/summary'),

  // ---- Config: pipelines / stages ----
  getPipelines: (): Promise<{ pipelines: CrmPipelineConfig[]; count: number }> =>
    api.get<{ pipelines: CrmPipelineConfig[]; count: number }>('/crm/pipelines'),

  createPipeline: (data: CreatePipelineInput): Promise<CrmPipelineConfig> =>
    api.post<{ pipeline: CrmPipelineConfig }>('/crm/pipelines', data).then((r) => r.pipeline),

  updateStage: (id: number, data: UpdateStageInput): Promise<CrmStage> =>
    api.put<{ stage: CrmStage }>(`/crm/stages/${id}`, data).then((r) => r.stage),

  // ---- Revenue Command Center ----
  getCommandCenter: (): Promise<CrmCommandCenter> =>
    api.get<CrmCommandCenter>('/crm/command-center'),

  // ---- Customer 360 (CRM overlay merged with AR/orders/invoices) ----
  getCustomer360: (customerId: number): Promise<CrmCustomer360> =>
    api.get<CrmCustomer360>(`/crm/customer/${customerId}/360`),

  // ---- Opportunity Workspace ----
  getOpportunityWorkspace: (id: number): Promise<OpportunityWorkspace> =>
    api.get<OpportunityWorkspace>(`/crm/opportunity/${id}/workspace`),

  // ---- My Day ----
  getMyDay: (): Promise<MyDay> => api.get<MyDay>('/crm/my-day'),

  // ---- Contacts (buying committee) ----
  getContacts: (customerId: number): Promise<{ contacts: Contact[]; count: number }> =>
    api.get<{ contacts: Contact[]; count: number }>(`/crm/customer/${customerId}/contacts`),

  createContact: (customerId: number, data: CreateContactInput): Promise<Contact> =>
    api.post<{ contact: Contact }>(`/crm/customer/${customerId}/contacts`, data).then((r) => r.contact),

  updateContact: (id: number, data: UpdateContactInput): Promise<Contact> =>
    api.put<{ contact: Contact }>(`/crm/contacts/${id}`, data).then((r) => r.contact),

  deleteContact: (id: number): Promise<void> =>
    api.delete<MessageOnly>(`/crm/contacts/${id}`).then(() => undefined),
};

export default crmService;
